export type RumMetric = {
  name: string;
  value: number;
  unit: 'ms' | 'score';
  rating?: 'good' | 'needs-improvement' | 'poor';
  route?: string;
  detail?: Record<string, string | number | boolean | null>;
};

const MAX_METRICS = 80;
let initialized = false;

function currentRoute() {
  if (typeof window === 'undefined') return '/';
  return window.location.pathname || '/';
}

function ratingFor(name: string, value: number): RumMetric['rating'] {
  if (name === 'largest-contentful-paint') {
    return value <= 2500 ? 'good' : value <= 4000 ? 'needs-improvement' : 'poor';
  }
  if (name === 'cumulative-layout-shift') {
    return value <= 0.1 ? 'good' : value <= 0.25 ? 'needs-improvement' : 'poor';
  }
  if (name === 'interaction-to-next-paint') {
    return value <= 200 ? 'good' : value <= 500 ? 'needs-improvement' : 'poor';
  }
  return undefined;
}

export function reportRumMetric(metric: RumMetric) {
  if (typeof window === 'undefined') return;
  const normalized: RumMetric = {
    ...metric,
    route: metric.route ?? currentRoute(),
    rating: metric.rating ?? ratingFor(metric.name, metric.value),
  };
  const metrics = (window.__SOLARGABAY_RUM__ ??= []);
  metrics.push(normalized);
  if (metrics.length > MAX_METRICS) metrics.splice(0, metrics.length - MAX_METRICS);
  window.dispatchEvent(new CustomEvent('solargabay:rum', { detail: normalized }));
  window.gtag?.('event', 'performance_metric', {
    metric_name: normalized.name,
    metric_value: Math.round(normalized.value * 1000) / 1000,
    metric_unit: normalized.unit,
    metric_rating: normalized.rating ?? 'unrated',
    route: normalized.route ?? '/',
  });
}

function observePerformanceEntry(
  type: string,
  callback: (entries: PerformanceEntry[]) => void,
  options?: PerformanceObserverInit,
) {
  if (typeof PerformanceObserver === 'undefined') return;
  try {
    const observer = new PerformanceObserver((list) => callback(list.getEntries()));
    observer.observe(options ?? { type, buffered: true });
    return observer;
  } catch {
    return undefined;
  }
}

export function initializeRealUserMonitoring() {
  if (initialized || typeof window === 'undefined') return;
  initialized = true;

  const navigation = performance.getEntriesByType('navigation')[0] as
    PerformanceNavigationTiming | undefined;
  if (navigation) {
    reportRumMetric({ name: 'time-to-first-byte', value: navigation.responseStart, unit: 'ms' });
    reportRumMetric({
      name: 'dom-content-loaded',
      value: navigation.domContentLoadedEventEnd,
      unit: 'ms',
    });
  }

  const paintObserver = observePerformanceEntry('paint', (entries) => {
    const fcp = entries.find((entry) => entry.name === 'first-contentful-paint');
    if (fcp) reportRumMetric({ name: 'first-contentful-paint', value: fcp.startTime, unit: 'ms' });
  });

  let largestContentfulPaint = 0;
  const lcpObserver = observePerformanceEntry('largest-contentful-paint', (entries) => {
    const latest = entries.at(-1);
    if (latest) largestContentfulPaint = latest.startTime;
  });

  let cumulativeLayoutShift = 0;
  const clsObserver = observePerformanceEntry('layout-shift', (entries) => {
    for (const entry of entries as Array<
      PerformanceEntry & { value?: number; hadRecentInput?: boolean }
    >) {
      if (!entry.hadRecentInput) cumulativeLayoutShift += entry.value ?? 0;
    }
  });

  let interactionToNextPaint = 0;
  const inpObserver = observePerformanceEntry(
    'event',
    (entries) => {
      for (const entry of entries)
        interactionToNextPaint = Math.max(interactionToNextPaint, entry.duration);
    },
    { type: 'event', buffered: true, durationThreshold: 40 } as PerformanceObserverInit,
  );

  const flush = () => {
    if (largestContentfulPaint > 0) {
      reportRumMetric({
        name: 'largest-contentful-paint',
        value: largestContentfulPaint,
        unit: 'ms',
      });
      largestContentfulPaint = 0;
    }
    reportRumMetric({
      name: 'cumulative-layout-shift',
      value: cumulativeLayoutShift,
      unit: 'score',
    });
    if (interactionToNextPaint > 0) {
      reportRumMetric({
        name: 'interaction-to-next-paint',
        value: interactionToNextPaint,
        unit: 'ms',
      });
    }
  };

  document.addEventListener(
    'visibilitychange',
    () => {
      if (document.visibilityState === 'hidden') flush();
    },
    { passive: true },
  );
  window.addEventListener('pagehide', flush, { once: true });

  window.addEventListener(
    'load',
    () => {
      requestAnimationFrame(() => {
        reportRumMetric({ name: 'window-load-complete', value: performance.now(), unit: 'ms' });
      });
    },
    { once: true },
  );

  window.addEventListener('pagehide', () => {
    paintObserver?.disconnect();
    lcpObserver?.disconnect();
    clsObserver?.disconnect();
    inpObserver?.disconnect();
  });
}

export function markHomepageContentRendered() {
  if (typeof window === 'undefined') return;
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      reportRumMetric({
        name: 'homepage-content-rendered',
        value: performance.now(),
        unit: 'ms',
      });
    });
  });
}

export async function markHomepageHeroReady(src: string) {
  if (typeof window === 'undefined') return;
  const image = new Image();
  image.src = src;
  try {
    await image.decode();
    reportRumMetric({ name: 'homepage-hero-image-ready', value: performance.now(), unit: 'ms' });
  } catch {
    reportRumMetric({
      name: 'homepage-hero-image-error',
      value: performance.now(),
      unit: 'ms',
    });
  }
}
